/**
 * The board's per-token turn state and vitals (`projects/turn-state-on-the-map.md` 1.0, the
 * developer's rulings of 2026-09-25; the hex trim from `projects/hex-tokens.md`, 2026-09-26/29).
 * `pixiCombatBoard.ts`'s `renderTokenLook` calls {@link drawTokenChromeUnder} before the token sprite
 * and {@link drawTokenChrome} after it, both in the token's own container and footprint-local pixels
 * (`0..side`), so every mark rides a drag or a tween with the token.
 *
 * The token is a pointy-top hex (`pixiTokenSprite.ts`), and its vitals are **trim**: bands just
 * outside the hex edges, laid out by `hexGeometry.ts`'s {@link tokenHex}. Bands on adjacent edges
 * meet mitred at the hex's own corner, like picture-frame trim.
 *
 * What it draws, all from a {@link TokenChrome} (`lib/encounter/tokenChrome.ts`) and nothing off the
 * raw combatant:
 *   - **The peak mark** ({@link peakMarkFor}, developer 2026-09-29): a unit that can still go this
 *     round wears a side-colour chevron on the hex's top peak, glowing while it is its side's pick
 *     (the board pulses the `peak-glow` child); a multi-activation NPC wears pips for its remaining
 *     activations instead; a unit that went wears a small grey check, and its side ring turns grey
 *     (`pixiTokenSprite.ts`'s `spent`). ACTIVE draws no peak mark: an ACTIVE tag hangs from the
 *     peak instead, over a red hex frame in the trim band and a red wash under the token. The frame
 *     is still and `--combat-accent` red for every side, so it never reads as the selection halo
 *     (breathing, cream) or the "mine" ring (inside, side colour). The board adds the loud half, a
 *     pulsing beacon ring {@link ACTIVE_BEACON_GAP} px outside the frame (`pixiCombatBoard.ts`,
 *     `turn-presence` Decision 1 C); {@link activeBeaconId} and {@link activeBeaconRadius} are its
 *     target and size.
 *   - **HP** along the two lower edges, filling from the lower-left corner. Always red: deep red at
 *     full, hotter as it drops.
 *   - **Heat** up the right edge from the lower-right corner, where HP ends: amber, orange at the
 *     Danger Zone, white-hot at the cap. The empty track shows on every unit with a heat track.
 *   - **Structure** down the left edge, one segment per point, lit from the bottom. Always shown for
 *     a unit with a structure track.
 *   - **The glow ramp is gated** (developer, 2026-09-29): HP glows once it is at or below half, heat
 *     once it reaches the Danger Zone, building to full at 1 HP or full heat ({@link hpGlow},
 *     {@link heatGlow}). A healthy, cool unit never glows, so a glow always means something.
 *   - **Overshield** is a numbered green shield badge in the top-left corner.
 *   - **Status letters** in the top-right corner (Size 2+) or a row above the peak (Size 1).
 *
 * The hex and its trim span a little past the footprint (`hexGeometry.ts`'s `TOKEN_SCALE`), and the
 * label solver pads each token rect by that overflow (`tokenOverflow`). A size-1 unit's status
 * letters keep the row above the drawn hex; {@link tokenChromeObstacle} reports that row's rect so
 * the label solver keeps names off it.
 */
import { BlurFilter, Container, Graphics, Text, TextStyle } from "pixi.js";
import type { TokenChrome, TokenTrack } from "../../lib/encounter/tokenChrome";
import type { Rect } from "./labelLayout";
import { HEX, TRIM_OUTLINE, bandQuad, growHex, hexPoints, hexVertex, peakAnchor, runSpans, tokenHex, tokenOverflow, type TokenHex } from "./hexGeometry";

/** `--combat-accent`: the ACTIVE frame + wash, the ACTIVE tag and the board's beacon. */
export const STATE_COLOR = 0xc0240f;
const INK = 0xe9e6df;
const LINE = 0x2a2a30;
const BADGE_BG = 0x111114;
const SHIELD_COLOR = 0x3ad29f;
/** A lost structure point. */
const STRUCTURE_LOST = 0x3a3a40;
const HP_FULL = 0x9c2216;
const HP_DIRE = 0xff4a2a;
const HEAT_COOL = 0xc98a1c;
const HEAT_DANGER = 0xff8a1f;
const HEAT_MAX = 0xfff0b8;
const ACTIVE_FRAME_W = 2.5;
const ACTIVE_WASH_ALPHA = 0.28;
/** A spent unit's side ring and check (`pixiTokenSprite.ts` reads the ring colour from here too). */
export const SPENT_GREY = 0x8d9199;
/** The peak mark's width at Size 1 and Size 2+; the chevron is half as tall, the check as tall. */
const PEAK_SMALL = 10;
const PEAK_BIG = 13;
/** The dark outline under every peak mark, so it reads on light terrain. */
const PEAK_OUTLINE = 2;
/** One activation pip's size and the gap between pips. */
const PIP_SMALL = 6;
const PIP_BIG = 7;
const PIP_GAP = 2;
/** The HP/heat and HP/structure separators' width, as a fraction of the trim's thickness. */
const TRIM_SEPARATOR = 0.4;
/** Gap between structure segments, in px along the edge. */
const SEGMENT_GAP = 1.2;
/** Glow strength at the gate (half HP, the Danger Zone), so the first glow is visible. */
const GLOW_FLOOR = 0.2;
const GLOW_LAYERS = 8;
/** Gap between the drawn hex's top peak and the size-1 status row above it. */
const ROW_GAP = 3;
const STATUS_BOX_SMALL = 9;
const STATUS_BOX_BIG = 11;
const STATUS_CAP_SMALL = 3;
const STATUS_CAP_BIG = 4;
const SHIELD_BADGE_SMALL = 9;
const SHIELD_BADGE_BIG = 12;

/** The ACTIVE tag's height at Size 1 and Size 2+; it straddles the top peak, inside the footprint. */
const ACTIVE_TAG_SMALL = 8;
const ACTIVE_TAG_BIG = 10;
/** How far the board's beacon ring sits outside the ACTIVE frame's edge, in px. */
export const ACTIVE_BEACON_GAP = 6;

/** Whether this token is drawn at size-1 scale (a 0.5 footprint renders as a full cell too). */
const isSmall = (side: number, cell: number): boolean => side <= cell;

/** The ACTIVE wash, drawn first so the token's art covers all but the hex's rim. */
export function drawTokenChromeUnder(container: Container, chrome: TokenChrome, geom: { side: number; cell: number }): void {
  if (chrome.state !== "active") return;
  const hex = tokenHex(geom.side, geom.cell);
  container.addChild(new Graphics().poly(hexPoints(hex.c, hex.c, hex.ringOuter), true).fill({ color: STATE_COLOR, alpha: ACTIVE_WASH_ALPHA }));
}

/**
 * What sits on the hex's top peak. `null` for a non-actor and for the ACTIVE unit, whose peak
 * turn-presence's ACTIVE tag owns. A multi-activation unit with turns left shows pips; any other
 * unit that has gone this round shows a check; the rest show the chevron. `glow` is "its side's
 * pick" (`ready`: nobody is acting and its side picks next).
 */
export type PeakMark = { kind: "chevron"; glow: boolean } | { kind: "pips"; remaining: number; max: number; glow: boolean } | { kind: "check" };

export function peakMarkFor(chrome: TokenChrome): PeakMark | null {
  if (chrome.state === null || chrome.state === "active") return null;
  const glow = chrome.state === "ready";
  const a = chrome.activations;
  if (a && a.used < a.max) return { kind: "pips", remaining: a.max - a.used, max: a.max, glow };
  if (chrome.state === "activated") return { kind: "check" };
  return { kind: "chevron", glow };
}

/** Everything else, drawn over the token's art. `accent` is the side colour the chevron and pips wear. */
export function drawTokenChrome(container: Container, chrome: TokenChrome, geom: { side: number; cell: number; accent: string }): void {
  const { side } = geom;
  const small = isSmall(side, geom.cell);
  const hex = tokenHex(side, geom.cell);
  if (chrome.state === "active") {
    // In the trim band, so the vitals draw over it and it shows along the free upper edges.
    const frame = new Graphics().poly(hexPoints(hex.c, hex.c, activeFrameRadius(hex)), true).stroke({ width: ACTIVE_FRAME_W, color: STATE_COLOR, join: "miter" });
    frame.label = "active-frame";
    container.addChild(frame);
  }
  drawVitals(container, chrome, hex);
  if (chrome.state === "active") container.addChild(activeTag(peakAnchor(hex), small ? ACTIVE_TAG_SMALL : ACTIVE_TAG_BIG));

  const mark = peakMarkFor(chrome);
  const markHalf = mark ? drawPeakMark(container, mark, { peak: peakAnchor(hex), small, accent: geom.accent }) : 0;
  if (chrome.overshield > 0) {
    const b = shieldBadge(chrome.overshield, small ? SHIELD_BADGE_SMALL : SHIELD_BADGE_BIG);
    // Inset so the outline's mitred corners stay inside the footprint.
    b.position.set(2, 2);
    container.addChild(b);
  }
  if (small) drawStatusRow(container, chrome, geom);
  else drawCornerStatuses(container, chrome.statuses, { side, top: 1, markRight: hex.c + Math.max(markHalf, PEAK_BIG / 2) + PEAK_OUTLINE });
}

/**
 * The rect the chrome occupies OUTSIDE the footprint, in footprint-local pixels, or `null` when
 * nothing leaves it: a size-1 unit's status row. The caller offsets it by the token's world position
 * and feeds it to `solveLabelLayout` as an obstacle.
 */
export function tokenChromeObstacle(chrome: TokenChrome, geom: { side: number; cell: number }): Rect | null {
  if (!isSmall(geom.side, geom.cell)) return null;
  const row = statusRow(chrome.statuses);
  if (row.length === 0) return null;
  const w = rowWidth(row.length);
  return { x: (geom.side - w) / 2, y: statusRowTop(geom), w, h: STATUS_BOX_SMALL };
}

/** The ACTIVE frame's circumradius, centred on the footprint: the middle of the trim band. */
function activeFrameRadius(hex: TokenHex): number {
  return growHex(hex.trimIn, hex.trim / 2);
}

/** The beacon ring's circumradius around a token of `side`: {@link ACTIVE_BEACON_GAP} px outside the ACTIVE frame's outer edge. */
export function activeBeaconRadius(geom: { side: number; cell: number }): number {
  return growHex(activeFrameRadius(tokenHex(geom.side, geom.cell)), ACTIVE_FRAME_W / 2 + ACTIVE_BEACON_GAP);
}

/**
 * The one token the beacon rings: the token whose chrome reads ACTIVE, the same `state` that draws
 * the frame, so the board never derives the active unit a second way. `null` when none does (between
 * turns, or the actor is not in this viewer's frame).
 */
export function activeBeaconId(chromes: Iterable<readonly [string, TokenChrome | undefined]>): string | null {
  for (const [id, chrome] of chromes) if (chrome?.state === "active") return id;
  return null;
}

/**
 * The ACTIVE tag: red with white letters, hanging from the top peak into the hex, so it stays inside
 * the token rect the label solver already pads by `tokenOverflow` and adds nothing to
 * {@link tokenChromeObstacle}. Its width is the monospace advance, so it never measures the text.
 */
function activeTag(peak: { x: number; y: number }, h: number): Container {
  const font = h * 0.78;
  const w = Math.ceil("ACTIVE".length * font * 0.62) + 4;
  const cx = peak.x;
  const top = peak.y;
  const tag = new Container();
  tag.label = "active-tag";
  tag.addChild(new Graphics().rect(cx - w / 2, top, w, h).fill(STATE_COLOR).stroke({ width: 1, color: 0x000000, alignment: 1 }));
  const t = new Text({ text: "ACTIVE", style: mono(font, 0xffffff) });
  t.label = "active-tag-text";
  t.anchor.set(0.5);
  t.position.set(cx, top + h / 2 + 0.5);
  tag.addChild(t);
  return tag;
}

/** HP glow strength, 0..1, on the gated ramp: 0 above half HP, {@link GLOW_FLOOR} at half, 1 at 1 HP or below. */
export function hpGlow(hp: number, maxHp: number): number {
  const half = maxHp / 2;
  return hp > half ? 0 : glowRamp(half - hp, half - 1);
}

/** Heat glow strength, 0..1: 0 below the Danger Zone (half the cap), {@link GLOW_FLOOR} at it, 1 at the cap. */
export function heatGlow(heat: TokenTrack): number {
  const danger = heat.max / 2;
  return heat.cur < danger ? 0 : glowRamp(heat.cur - danger, heat.max - danger);
}

function glowRamp(past: number, span: number): number {
  return span <= 0 ? 1 : GLOW_FLOOR + (1 - GLOW_FLOOR) * Math.min(1, past / span);
}

function drawVitals(container: Container, chrome: TokenChrome, hex: TokenHex): void {
  if (chrome.hp !== null && chrome.maxHp) {
    const glow = hpGlow(chrome.hp, chrome.maxHp);
    const trim = trimRun(hex, [HEX.lowerLeft, HEX.bottom, HEX.lowerRight], { frac: chrome.hp / chrome.maxHp, color: lerpColor(HP_FULL, HP_DIRE, glow), glow, glowWhole: true });
    trim.label = "trim-hp";
    container.addChild(trim);
  }
  if (chrome.heat) {
    const h = chrome.heat.cur / chrome.heat.max;
    const color = h < 0.5 ? lerpColor(HEAT_COOL, HEAT_DANGER, h / 0.5) : lerpColor(HEAT_DANGER, HEAT_MAX, (h - 0.5) / 0.5);
    const trim = trimRun(hex, [HEX.lowerRight, HEX.upperRight], { frac: h, color, glow: heatGlow(chrome.heat), glowWhole: false });
    trim.label = "trim-heat";
    container.addChild(trim);
  }
  if (chrome.structure) {
    const trim = trimSegments(hex, { from: HEX.lowerLeft, to: HEX.upperLeft }, chrome.structure);
    trim.label = "trim-structure";
    container.addChild(trim);
  }
  // A black separator on the mitre where HP meets heat (lower-right) and structure (lower-left),
  // drawn whenever both tracks are, empty or not (developer, 2026-09-29: "black borders separating
  // the health bar from the heat bar and from the struct bar").
  const hpDrawn = chrome.hp !== null && !!chrome.maxHp;
  if (hpDrawn && chrome.heat) container.addChild(trimSeparator(hex, HEX.lowerRight));
  if (hpDrawn && chrome.structure) container.addChild(trimSeparator(hex, HEX.lowerLeft));
}

/** The separator along corner `k`'s bisector, from the trim's inner outline to its outer edge,
 *  as thick as a fraction of the trim so it scales with the token. Labelled `trim-sep`. */
function trimSeparator(hex: TokenHex, k: number): Graphics {
  const [x1, y1] = hexVertex(hex.c, growHex(hex.trimIn, -TRIM_OUTLINE), k);
  const [x2, y2] = hexVertex(hex.c, hex.trimOut, k);
  const g = new Graphics().moveTo(x1, y1).lineTo(x2, y2).stroke({ width: hex.trim * TRIM_SEPARATOR, color: 0x000000 });
  g.label = "trim-sep";
  return g;
}

type Span = { from: number; to: number; t0: number; t1: number };

function bands(hex: TokenHex, spans: Span[], grow: { in: number; out: number } = { in: 0, out: 0 }): Graphics {
  const band = { rin: growHex(hex.trimIn, -grow.in), rout: growHex(hex.trimOut, grow.out) };
  const g = new Graphics();
  for (const s of spans) g.poly(bandQuad(hex.c, band, s), true);
  return g;
}

/**
 * `frac` of a run of edges filled in `color` over the dark track, the whole run on a dark under-band.
 * A glow sits under it all: along the whole run when `glowWhole` (HP: the base burns), else only
 * under the filled part (heat).
 */
function trimRun(hex: TokenHex, path: number[], opts: { frac: number; color: number; glow: number; glowWhole: boolean }): Container {
  const out = new Container();
  const whole = runSpans(path, 1);
  const filled = runSpans(path, opts.frac);
  if (opts.glow > 0 && filled.length > 0) out.addChild(glowUnder(hex, opts.glowWhole ? whole : filled, { color: opts.color, strength: opts.glow }));
  out.addChild(bands(hex, whole, { in: TRIM_OUTLINE, out: TRIM_OUTLINE }).fill({ color: 0x000000, alpha: 0.65 }));
  out.addChild(bands(hex, whole).fill(LINE));
  if (filled.length > 0) out.addChild(bands(hex, filled).fill(opts.color));
  return out;
}

/**
 * The glow: {@link GLOW_LAYERS} nested bands, each wider and fainter than the last, out to a spread
 * that grows with `strength`, then a light blur to melt their steps. A strong `BlurFilter` alone
 * renders as hard offset copies in Chrome, so the layers build the falloff and the blur only smooths.
 */
function glowUnder(hex: TokenHex, spans: Span[], opts: { color: number; strength: number }): Container {
  const spread = hex.trim * (0.8 + 2.6 * opts.strength);
  const glow = new Container();
  glow.label = "glow";
  for (let k = GLOW_LAYERS; k >= 1; k--) {
    const e = (spread * k) / GLOW_LAYERS;
    const alpha = (0.05 + 0.45 * opts.strength) * (1 - (k - 1) / GLOW_LAYERS);
    glow.addChild(bands(hex, spans, { in: e * 0.5, out: e }).fill({ color: opts.color, alpha }));
  }
  glow.filters = [new BlurFilter({ strength: 2, quality: 4 })];
  return glow;
}

/** One segment per point along an edge, lit from its start, on a dark under-band. */
function trimSegments(hex: TokenHex, edge: { from: number; to: number }, track: TokenTrack): Container {
  const [ax, ay] = hexVertex(hex.c, hex.trimIn, edge.from);
  const [bx, by] = hexVertex(hex.c, hex.trimIn, edge.to);
  const gap = SEGMENT_GAP / Math.hypot(bx - ax, by - ay);
  const seg = (1 - gap * (track.max - 1)) / track.max;
  const spans = Array.from({ length: track.max }, (_, i): Span => ({ ...edge, t0: i * (seg + gap), t1: i * (seg + gap) + seg }));
  const out = new Container();
  out.addChild(bands(hex, spans, { in: TRIM_OUTLINE, out: TRIM_OUTLINE }).fill({ color: 0x000000, alpha: 0.65 }));
  out.addChild(bands(hex, spans.slice(track.cur)).fill(STRUCTURE_LOST));
  if (track.cur > 0) out.addChild(bands(hex, spans.slice(0, track.cur)).fill(INK));
  return out;
}

function lerpColor(a: number, b: number, t: number): number {
  const mix = (shift: number): number => {
    const x = (a >> shift) & 255;
    return Math.round(x + (((b >> shift) & 255) - x) * t) << shift;
  };
  return mix(16) | mix(8) | mix(0);
}

/**
 * Draw the peak mark hanging from the top peak (its outline's top on the peak, so it stays inside
 * the drawn extent the label solver pads for) and return its half-width, which the Size 2+ corner statuses keep clear of. A glowing mark gets a
 * blurred `peak-glow` child directly on `container`, which the board's ticker pulses.
 */
function drawPeakMark(container: Container, mark: PeakMark, at: { peak: { x: number; y: number }; small: boolean; accent: string }): number {
  const { x, y } = at.peak;
  const w = at.small ? PEAK_SMALL : PEAK_BIG;
  const g = new Graphics();
  g.label = "peak-mark";
  let half = w / 2;
  if (mark.kind === "chevron") {
    // A tab pointing up, its apex on the peak.
    const h = w * 0.6;
    const top = y + PEAK_OUTLINE / 2;
    const pts = [x, top, x + w / 2, top + h, x - w / 2, top + h];
    if (mark.glow) addPeakGlow(container, (q) => q.poly(pts, true), at.accent);
    g.poly(pts, true).stroke({ width: PEAK_OUTLINE, color: 0x000000, join: "round" }).poly(pts, true).fill(at.accent);
  } else if (mark.kind === "check") {
    const r = w * 0.42;
    const cy = y + r + 0.5;
    g.circle(x, cy, r).fill({ color: BADGE_BG, alpha: 0.9 }).stroke({ width: 1, color: SPENT_GREY });
    g.moveTo(x - r * 0.5, cy).lineTo(x - r * 0.1, cy + r * 0.4).lineTo(x + r * 0.55, cy - r * 0.4).stroke({ width: at.small ? 1.6 : 2, color: SPENT_GREY, cap: "round", join: "round" });
  } else {
    const p = at.small ? PIP_SMALL : PIP_BIG;
    const rowW = mark.max * p + (mark.max - 1) * PIP_GAP;
    half = rowW / 2 + 1;
    const cy = y + 1 + p / 2;
    const backing = (q: Graphics): Graphics => q.roundRect(x - half, y, 2 * half, p + 2, 2);
    if (mark.glow) addPeakGlow(container, backing, at.accent);
    backing(g).fill({ color: BADGE_BG, alpha: 0.9 });
    for (let i = 0; i < mark.max; i++) {
      const cx = x - rowW / 2 + p / 2 + i * (p + PIP_GAP);
      const pts = [cx, cy - p / 2, cx + p / 2, cy, cx, cy + p / 2, cx - p / 2, cy];
      if (i < mark.remaining) g.poly(pts, true).fill(at.accent);
      else g.poly(pts, true).stroke({ width: 1, color: at.accent });
    }
  }
  container.addChild(g);
  return half;
}

/** A soft side-colour glow behind the peak mark; its alpha is the board ticker's to pulse. */
function addPeakGlow(container: Container, shape: (g: Graphics) => Graphics, accent: string): void {
  const glow = shape(new Graphics()).fill({ color: accent, alpha: 0.9 }).stroke({ width: 6, color: accent, alpha: 0.7, join: "round" });
  glow.label = "peak-glow";
  glow.filters = [new BlurFilter({ strength: 4 })];
  container.addChild(glow);
}

const ROW_ITEM_GAP = 2;
const statusRow = (statuses: string[]): string[] => cappedStatuses(statuses, STATUS_CAP_SMALL);
const rowWidth = (n: number): number => n * STATUS_BOX_SMALL + ROW_ITEM_GAP * (n - 1);

/** Size 1: the status row's top, {@link ROW_GAP} above the drawn hex's peak. */
function statusRowTop(geom: { side: number; cell: number }): number {
  return -tokenOverflow(geom.side, geom.cell).y - ROW_GAP - STATUS_BOX_SMALL;
}

/** Size 1: the status letters in a row centred above the drawn hex. */
function drawStatusRow(container: Container, chrome: TokenChrome, geom: { side: number; cell: number }): void {
  const row = statusRow(chrome.statuses);
  let x = (geom.side - rowWidth(row.length)) / 2;
  for (const label of row) {
    container.addChild(statusBox(x, statusRowTop(geom), { label, size: STATUS_BOX_SMALL }));
    x += STATUS_BOX_SMALL + ROW_ITEM_GAP;
  }
}

/** Size 2+: the status letters right-aligned in the top-right corner, as many as fit right of the
 *  peak mark (the last slot reads `+n` for the rest). */
function drawCornerStatuses(container: Container, statuses: string[], at: { side: number; top: number; markRight: number }): void {
  const step = STATUS_BOX_BIG + 1;
  const fit = Math.max(1, Math.min(STATUS_CAP_BIG, Math.floor((at.side - 1 - at.markRight) / step)));
  cappedStatuses(statuses, fit).forEach((label, i) => container.addChild(statusBox(at.side - 1 - (i + 1) * step + 1, at.top, { label, size: STATUS_BOX_BIG })));
}

/** `cap` letters at most; over the cap, the last slot reads `+n` for the rest. */
function cappedStatuses(statuses: string[], cap: number): string[] {
  if (statuses.length <= cap) return statuses;
  const shown = statuses.slice(0, cap - 1);
  return [...shown, `+${statuses.length - shown.length}`];
}

function mono(size: number, fill: number = INK): TextStyle {
  return new TextStyle({ fill, fontFamily: "JetBrains Mono, monospace", fontWeight: "800", fontSize: size });
}

function statusBox(x: number, y: number, it: { label: string; size: number }): Container {
  const c = new Container();
  c.addChild(new Graphics().rect(x, y, it.size, it.size).fill(BADGE_BG).stroke({ width: 1, color: 0x8a877f }));
  const t = new Text({ text: it.label, style: mono(it.label.length > 1 ? it.size * 0.6 : it.size * 0.78) });
  t.anchor.set(0.5);
  t.x = x + it.size / 2;
  t.y = y + it.size / 2 + 0.5;
  c.addChild(t);
  return c;
}

/** A green shield with the overshield count, the unit sheet's shield colour. */
function shieldBadge(n: number, size: number): Container {
  const c = new Container();
  const h = size * 1.15;
  const pts = [0, 0, size, 0, size, h * 0.55, size / 2, h, 0, h * 0.55];
  c.addChild(new Graphics().poly(pts, true).stroke({ width: 2.5, color: 0x000000, join: "miter" }).poly(pts, true).fill(SHIELD_COLOR));
  const t = new Text({ text: String(n), style: mono(size * 0.78, 0x06110c) });
  t.anchor.set(0.5);
  t.position.set(size / 2, h * 0.42);
  c.addChild(t);
  return c;
}
